import React, { useEffect, useState } from "react";
import {
  AlertCircle,
  ArrowRight,
  CarFront,
  Cog,
  Copy,
  FileJson,
  Info,
  ListCheck,
  Loader,
  Loader2,
} from "lucide-react";

import {
  Car,
  User,
  MapPin,
  Calendar,
  ShieldCheck,
  Settings,
  FileText,
  Activity,
  CheckCircle2,
} from "lucide-react";

import { isAlphanumeric } from "validator";
import { useService } from "../../../../context/ServiceContext";
import { showErrorToast, showSuccessToast } from "../../../../utils/toastUtils";
import { fetchProfile } from "../../../../Services/profile/profileservice";
import Seo from "../../../../Components/SeoComponent/Seo";
import Header from "../../../../Components/Header";
import { getInsuranceDetailsSrv2 } from "../../../../Services/ApiTestService/apiTestService";

function VehicleInsurance() {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const [vehicleNo, setVehicleNo] = useState("");
  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);
  const { serviceName } = useService();

  const [profileData, setProfileData] = useState(null);
  let apiResponse;

  const isAlpha = (value) => {
    if (!value) return false;

    return isAlphanumeric(String(value).trim(), "en-IN", { ignore: " " });
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("Copied!"));

    setIsJsonCopied(true);
  };

  const fetchVehicleInsurance = async () => {
    if (!profileData) return;

    const validateVehicleNum = isAlpha(vehicleNo);

    if (!vehicleNo) {
      return setInputError("Vehicle Number is Required");
    }

    if (vehicleNo && !validateVehicleNum) {
      return setInputError(
        "⚠️ Please enter alpha numeric in Vehicle No. field",
      );
    }

    try {
      setInputError(null);
      setIsLoading(true);
      setResponse(null);

      const res = await getInsuranceDetailsSrv2({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        vehicle_number: vehicleNo.trim()?.toUpperCase(),
      });

      if (res) {
        apiResponse = res;
      }
      setResponse(apiResponse);

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else if (res?.status?.code === 422) {
        showErrorToast(res?.data?.message || "invalid details ");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "server error");
      } else if (!res.success) {
        showErrorToast(res?.data?.message || "verificaton failed");
      } else {
        showErrorToast(res?.data?.message || "something went wrong");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      <Header heading={serviceName} />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="w-full min-h-screen flex flex-col items-center justify-start pb-14">
        {/* ---------------- FORM ---------------- */}
        <div className="max-w-4xl w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-100">
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-slate-100 pb-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
              <CarFront size={24} />
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-800">
                {serviceName}
              </h2>
              <p className="text-sm text-slate-500">
                Verify vehicle details using the Vehicle number.
              </p>
            </div>
          </div>

          {/* Inputs */}
          <div className="mt-6  gap-6 flex justify-center items-center">
            {/* Vehicle Number */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Cog size={17} className="text-blue-600" />
                Vehicle Number
              </label>

              <input
                type="text"
                value={vehicleNo}
                onChange={(e) => setVehicleNo(e.target.value)}
                placeholder="Eg: GJ0***1256"
                className="w-full rounded-xl border uppercase border-slate-300 px-4 py-3 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </div>

            {/* Button */}
            <div className="mt-8 flex ">
              <button
                disabled={isLoading || !vehicleNo}
                onClick={fetchVehicleInsurance}
                className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-7 py-3 font-semibold text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    Verify Vehicle
                    <ArrowRight
                      size={18}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Error */}
          {inputError && (
            <div className="mt-5 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
              <AlertCircle size={18} />
              <span className="text-sm font-medium">{inputError}</span>
            </div>
          )}
        </div>

        {/* response message */}

        {response && (
          <div
            className={` ${response.status?.code === 500 || response.status?.code === 422 ? "text-red-500" : "text-green-600"} py-4 text-2xl font-bold`}
          >
            {response?.data?.message || response?.message}
          </div>
        )}

        {/* instructions */}

        <div className="w-full flex justify-center">
          <div className="bg-green-100 max-w-3xl border border-green-400 text-green-700 p-5 mt-12 rounded-2xl">
            <h3 className="font-bold flex items-center justify-start gap-2 text-lg mb-2">
              <Info /> Important Instructions{" "}
              <span className="text-red-600">*</span>
            </h3>

            <p className="text-sm leading-relaxed">
              Please enter a valid Vehicle Number. Clicking the Verify button.
              After successful verification, the system will return the response
              in structured format, including contact details associated with
              the provided details. You may review and copy the data for your
              reference or further use.
            </p>
          </div>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 w-full max-w-6xl">
            {/* -------- JSON VIEW -------- */}
            <div className="bg-gray-900 relative text-green-300 p-4 rounded-md overflow-auto">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-white top-4 right-8"
              >
                {isJsonCopied ? "copied" : <Copy />}
              </button>
              <p className="font-bold mb-2 text-2xl flex items-center justify-start md:justify-center gap-2 text-white">
                <FileJson /> JSON response
              </p>
              <hr className="mb-2" />{" "}
              <pre className="text-[16px]">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            <div className="bg-gray-200 p-2 rounded-md">
              <h2 className="text-2xl flex items-center justify-center gap-2 font-bold text-blue-500 mb-1">
                <ListCheck />
                Insurance Data
              </h2>

              {response?.status?.code === 200 && response?.data && (
                <div className="mt-6 p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
                    Vehicle Insurance Details
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Vehicle Info */}
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500 uppercase tracking-wider">
                        Vehicle Number
                      </span>
                      <span className="font-medium text-gray-900">
                        {response.data.vehicleNumber}
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500 uppercase tracking-wider">
                        Vehicle Class
                      </span>
                      <span className="font-medium text-gray-900">
                        {response.data.vehicleClass}
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500 uppercase tracking-wider">
                        Manufacturer
                      </span>
                      <span className="font-medium text-gray-900">
                        {response.data.vehicleManufacturerName}
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500 uppercase tracking-wider">
                        Model & Variant
                      </span>
                      <span className="font-medium text-gray-900">
                        {response.data.model} - {response.data.variantName}
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500 uppercase tracking-wider">
                        Registration Date
                      </span>
                      <span className="font-medium text-gray-900">
                        {response.data.regDate}
                      </span>
                    </div>

                    {/* Insurance Info */}
                    <div className="flex flex-col sm:col-span-2 md:col-span-1">
                      <span className="text-xs text-gray-500 uppercase tracking-wider">
                        Insurance Company
                      </span>
                      <span className="font-medium text-gray-900">
                        {response.data.vehicleInsuranceCompanyName}
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500 uppercase tracking-wider">
                        Policy Number
                      </span>
                      <span className="font-medium text-gray-900">
                        {response.data.vehicleInsurancePolicyNumber}
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500 uppercase tracking-wider">
                        Valid Upto
                      </span>
                      <span className="font-medium text-gray-900">
                        {response.data.vehicleInsuranceUpto}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default VehicleInsurance;
